// client/src/components/MicSettingsPanel.jsx
// Microphone settings dropdown — mic selector + real-time audio level visualizer.
// Uses Web Audio API to show live microphone input level like Chrome's permission dialog.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './micSettingsPanel.css';

const MicSettingsPanel = ({
  availableMics,
  selectedMicId,
  onSelectMic,
  onClose,
}) => {

  const [audioLevel,   setAudioLevel]   = useState(0);   // 0–100
  const [micError,     setMicError]     = useState('');

  const streamRef      = useRef(null);
  const analyserRef    = useRef(null);
  const animFrameRef   = useRef(null);
  const audioCtxRef    = useRef(null);

  // ── Start live audio level monitoring ─────────────────────────────────────
  const startLevelMonitor = useCallback(async (deviceId) => {
    // Stop existing stream first
    stopLevelMonitor();
    setMicError('');

    try {
      const constraints = deviceId && deviceId !== 'default'
        ? { audio: { deviceId: { exact: deviceId } } }
        : { audio: true };

      const stream     = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const audioCtx   = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;

      const source     = audioCtx.createMediaStreamSource(stream);
      const analyser   = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      source.connect(analyser);

      const dataArray  = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        // Average of all frequency bins → normalize to 0–100
        const avg = dataArray.reduce((s, v) => s + v, 0) / dataArray.length;
        setAudioLevel(Math.min(100, Math.round((avg / 128) * 100)));
        animFrameRef.current = requestAnimationFrame(tick);
      };

      animFrameRef.current = requestAnimationFrame(tick);

    } catch (err) {
      setMicError('Could not access microphone. Check permissions.');
      console.error('MicSettingsPanel level monitor error:', err);
    }
  }, []);

  const stopLevelMonitor = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  // Start monitoring when panel opens
  useEffect(() => {
    startLevelMonitor(selectedMicId);
    return () => stopLevelMonitor();
  }, []);

  // Restart when mic selection changes
  const handleSelectMic = (e) => {
    const id = e.target.value;
    onSelectMic(id);
    startLevelMonitor(id);
  };

  // Level bar color — green → yellow → red based on volume
  const barColor = audioLevel > 80 ? '#ef4444'
    : audioLevel > 50 ? '#f59e0b'
    : '#22c55e';

  return (
    <>
      {/* ── Faded background overlay ── */}
      <div className="msp-backdrop" onClick={onClose} />

      {/* ── Panel ── */}
      <div className="msp-panel">

        <div className="msp-header">
          <span className="msp-title">🎤 Microphone Settings</span>
          <button className="msp-close" onClick={onClose} title="Close">✕</button>
        </div>

        {/* Mic selector */}
        <div className="msp-section">
          <label className="msp-label">Select Microphone</label>
          {availableMics.length === 0 ? (
            <p className="msp-empty">No microphones found. Allow microphone access and try again.</p>
          ) : (
            <select
              className="msp-select"
              value={selectedMicId}
              onChange={handleSelectMic}
            >
              <option value="default">Default Microphone</option>
              {availableMics.map(mic => (
                <option key={mic.deviceId} value={mic.deviceId}>
                  {mic.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Live audio level */}
        <div className="msp-section">
          <label className="msp-label">Microphone Level</label>

          {micError ? (
            <p className="msp-error">{micError}</p>
          ) : (
            <div className="msp-level-container">
              {/* Mic icon */}
              <span className="msp-level-icon">🎙️</span>

              {/* Level bar track */}
              <div className="msp-level-track">
                <div
                  className="msp-level-fill"
                  style={{
                    width:      `${audioLevel}%`,
                    background: barColor,
                  }}
                />
              </div>

              {/* Numeric level */}
              <span className="msp-level-num">{audioLevel}%</span>
            </div>
          )}

          <p className="msp-hint">Speak to see your microphone level.</p>
        </div>

      </div>
    </>
  );
};

export default MicSettingsPanel;
